import React, { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import jsPDF from "jspdf";
import "jspdf-autotable";

//updated
export default function QuotePage() {
  // phonesData structure: { Brand: { phoneName: { providers: { provider: {financing, returnOption} } } } }
  const [phonesData, setPhonesData] = useState({});
  const brands = ["Apple", "Samsung", "Google"];
  const allProviders = ["Rogers", "Bell", "Telus", "Fido", "Virgin", "Koodo", "Freedom"];
  const noReturnProviders = ["Fido", "Virgin", "Koodo"];

  // For user selection
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedPhone, setSelectedPhone] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");

  // Quote selections array
  const [selections, setSelections] = useState([]);

  // Fetch phonesData from Firestore on mount
  useEffect(() => {
    const fetchPhonesDoc = async () => {
      try {
        const snap = await getDoc(doc(db, "pricing", "phones"));
        if (snap.exists()) {
          setPhonesData(snap.data());
        } else {
          console.log("No phones doc found.");
        }
      } catch (error) {
        console.error("Error fetching phones doc:", error);
      }
    };
    fetchPhonesDoc();
  }, []);

  // Add selection based on chosen brand, phone, provider
  const addSelection = () => {
    if (!selectedBrand || !selectedPhone || !selectedProvider) {
      alert("Please select brand, phone, and provider.");
      return;
    }
    const phoneObj = phonesData[selectedBrand][selectedPhone];
    if (!phoneObj) return;

    const provData = phoneObj.providers?.[selectedProvider] || {};
    const financing = provData.financing || 0;
    const returnOption = noReturnProviders.includes(selectedProvider)
      ? ""
      : provData.returnOption || 0;

    const newItem = {
      brand: selectedBrand,
      phone: selectedPhone,
      provider: selectedProvider,
      financing: financing.toString(),
      returnOption: returnOption.toString(),
      planPrice: "",
      data: "",
      other: ""
    };
    setSelections([...selections, newItem]);
  };

  // Handlers for manual editing in the table
  const handleFinancingChange = (idx, value) => {
    setSelections((prev) => {
      const updated = [...prev];
      updated[idx].financing = value;
      return updated;
    });
  };

  const handleReturnChange = (idx, value) => {
    setSelections((prev) => {
      const updated = [...prev];
      updated[idx].returnOption = value;
      return updated;
    });
  };

  const handlePlanChange = (idx, value) => {
    setSelections((prev) => {
      const updated = [...prev];
      updated[idx].planPrice = value;
      return updated;
    });
  };

  const handleDataChange = (idx, value) => {
    setSelections((prev) => {
      const updated = [...prev];
      updated[idx].data = value;
      return updated;
    });
  };

  const handleOtherChange = (idx, value) => {
    setSelections((prev) => {
      const updated = [...prev];
      updated[idx].other = value;
      return updated;
    });
  };

  // Generate PDF Quote using current table values
  const generatePdfQuote = () => {
    if (selections.length === 0) {
      alert("No quote items to generate PDF.");
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Walmart Wireless Quote", 14, 22);
    doc.setFontSize(12);

    const head = [["Brand", "Phone", "Provider", "Financing", "Return", "Plan", "Data", "Other"]];
    const body = [];

    selections.forEach((item) => {
      const finVal = parseFloat(item.financing) || 0;
      const retVal = noReturnProviders.includes(item.provider)
        ? ""
        : (parseFloat(item.returnOption) || 0);
      body.push([
        item.brand,
        item.phone,
        item.provider,
        finVal ? `$${finVal.toFixed(2)}` : "",
        retVal !== "" ? `$${retVal.toFixed(2)}` : "",
        item.planPrice,
        item.data,
        item.other
      ]);
      body.push([
        "Total",
        "",
        "",
        finVal ? `$${(finVal * 24).toFixed(2)}` : "",
        retVal !== "" ? `$${(retVal * 24).toFixed(2)}` : "",
        "",
        "",
        ""
      ]);
    });

    doc.autoTable({
      head,
      body,
      startY: 30,
      theme: "grid"
    });
    doc.save("quote.pdf");
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 flex flex-col items-center">
      <h1 className="text-4xl font-bold mb-6">Walmart Wireless Quote</h1>

      {/* Selection Form */}
      <div className="bg-white p-6 rounded shadow-md max-w-xl w-full mb-6">
        <h2 className="text-2xl font-semibold mb-4 text-center">Select Brand, Phone & Provider</h2>
        <div className="flex flex-col gap-4 mb-4">
          {/* Brand Dropdown */}
          <div>
            <label className="block text-gray-700 mb-1">Brand</label>
            <select
              className="w-full p-2 border rounded"
              value={selectedBrand}
              onChange={(e) => {
                setSelectedBrand(e.target.value);
                setSelectedPhone("");
              }}
            >
              <option value="">-- choose brand --</option>
              {brands.map((b) => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>
          </div>
          {/* Phone Dropdown */}
          <div>
            <label className="block text-gray-700 mb-1">Phone</label>
            <select
              className="w-full p-2 border rounded"
              value={selectedPhone}
              onChange={(e) => setSelectedPhone(e.target.value)}
              disabled={!selectedBrand}
            >
              <option value="">-- choose phone --</option>
              {selectedBrand &&
                phonesData[selectedBrand] &&
                Object.keys(phonesData[selectedBrand]).map((pn) => (
                  <option key={pn} value={pn}>{pn}</option>
                ))}
            </select>
          </div>
          {/* Provider Dropdown */}
          <div>
            <label className="block text-gray-700 mb-1">Provider</label>
            <select
              className="w-full p-2 border rounded"
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              <option value="">-- choose provider --</option>
              {allProviders.map((prov) => (
                <option key={prov} value={prov}>{prov}</option>
              ))}
            </select>
          </div>
        </div>
        <button
          onClick={addSelection}
          className="w-full bg-black text-white py-2 rounded hover:bg-gray-800"
        >
          Add to Quote
        </button>
      </div>

      {/* Quote Table */}
      {selections.length > 0 && (
        <div className="bg-white p-6 rounded shadow-md max-w-4xl w-full">
          <h2 className="text-2xl font-semibold mb-4 text-center">Quote Details</h2>
          <table className="w-full border border-gray-300 text-center">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="border px-4 py-2">Brand</th>
                <th className="border px-4 py-2">Phone</th>
                <th className="border px-4 py-2">Provider</th>
                <th className="border px-4 py-2">Financing</th>
                <th className="border px-4 py-2">Return</th>
                <th className="border px-4 py-2">Plan</th>
                <th className="border px-4 py-2">Data</th>
                <th className="border px-4 py-2">Other</th>
              </tr>
            </thead>
            <tbody>
              {selections.map((item, index) => {
                const finVal = parseFloat(item.financing) || 0;
                const retVal = noReturnProviders.includes(item.provider)
                  ? ""
                  : parseFloat(item.returnOption) || 0;
                return (
                  <React.Fragment key={index}>
                    <tr className="bg-gray-50">
                      <td className="border px-4 py-2">{item.brand}</td>
                      <td className="border px-4 py-2">{item.phone}</td>
                      <td className="border px-4 py-2">{item.provider}</td>
                      <td className="border px-4 py-2">
                        <input
                          type="number"
                          className="w-20 p-1 border rounded text-center"
                          value={item.financing}
                          onChange={(e) => handleFinancingChange(index, e.target.value)}
                        />
                      </td>
                      <td className="border px-4 py-2">
                        {noReturnProviders.includes(item.provider) ? (
                          <span className="text-gray-500">—</span>
                        ) : (
                          <input
                            type="number"
                            className="w-20 p-1 border rounded text-center"
                            value={item.returnOption}
                            onChange={(e) => handleReturnChange(index, e.target.value)}
                          />
                        )}
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="text"
                          className="w-20 p-1 border rounded text-center"
                          value={item.planPrice}
                          onChange={(e) => handlePlanChange(index, e.target.value)}
                        />
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="text"
                          className="w-20 p-1 border rounded text-center"
                          value={item.data}
                          onChange={(e) => handleDataChange(index, e.target.value)}
                        />
                      </td>
                      <td className="border px-4 py-2">
                        <input
                          type="text"
                          className="w-20 p-1 border rounded text-center"
                          value={item.other}
                          onChange={(e) => handleOtherChange(index, e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr className="bg-gray-100 font-bold">
                      <td className="border px-4 py-2">Total</td>
                      <td className="border px-4 py-2"></td>
                      <td className="border px-4 py-2"></td>
                      <td className="border px-4 py-2">
                        {finVal ? `$${(finVal * 24).toFixed(2)}` : ""}
                      </td>
                      <td className="border px-4 py-2">
                        {retVal !== "" ? `$${(retVal * 24).toFixed(2)}` : ""}
                      </td>
                      <td className="border px-4 py-2"></td>
                      <td className="border px-4 py-2"></td>
                      <td className="border px-4 py-2"></td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          <button
            onClick={generatePdfQuote}
            className="w-full bg-black text-white py-2 rounded hover:bg-gray-800 mt-4"
          >
            Generate PDF Quote
          </button>
        </div>
      )}
    </div>
  );
}
